import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import InviteManager from '../components/InviteManager';
import UserProfileCard from '../components/cards/UserProfileCard';
import OrganizationManager from '../components/OrganizationManager';
import DrillLibraryManager from '../components/DrillLibraryManager';
import DashboardNavigation, { AvatarMenu } from '../components/DashboardNavigation';
import MobileHeaderDropdown from '../components/MobileHeaderDropdown';
import ImpersonationBanner from '../components/ImpersonationBanner';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import AssignmentTrackingTab from '../components/AssignmentTrackingTab';
import ParentInviteManager from '../components/ParentInviteManager';
import PlayerTagManager from '../components/PlayerTagManager';
import DrillRoutineManager from '../components/DrillRoutineManager';
import StatCategoryManager from '../components/StatCategoryManager';
import PerformanceMetricsDashboard from '../components/PerformanceMetricsDashboard';
import DashboardTabbedNavigation from '../components/DashboardTabbedNavigation';
import PlayerCard from '../components/cards/PlayerCard';

const AdminPage = () => {
  const { profile, realProfile, startImpersonation } = useAuth();
  const { isDesktop } = useResponsive();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('users-access');
  const [activeSubTab, setActiveSubTab] = useState('users');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    id: '',
    email: '',
    full_name: '',
    role: 'player',
    organization_id: '',
    team_id: '',
    family_id: '',
    birth_date: '',
    phone: '',
  });

  // Dashboard sections configuration for Admin
  const sections = [
    { id: 'roster', label: 'Team Roster' },
    {
      id: 'drills',
      label: 'Drills',
      icon: '🎯',
      subTabs: [
        { id: 'library', label: 'Drill Library' },
        { id: 'routines', label: 'Drill Routines' },
      ],
    },
    {
      id: 'performance',
      label: 'Performance',
      icon: '📊',
      subTabs: [
        { id: 'tracking', label: 'Assignment Tracking' },
        { id: 'metrics', label: 'Performance Metrics' },
      ],
    },
    {
      id: 'users-access',
      label: 'Users & Access',
      icon: '👥',
      subTabs: [
        { id: 'users', label: 'User Management' },
        { id: 'invites', label: 'Invite Management' },
        { id: 'parent-invites', label: 'Parent Invites' },
        { id: 'player-tags', label: 'Player Tags' },
      ],
    },
    {
      id: 'org-settings',
      label: 'Organization Settings',
      icon: '⚙️',
      subTabs: [
        { id: 'organization', label: 'Organization Management' },
        { id: 'stat-categories', label: 'Stat Categories' },
      ],
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      loadPlayers(selectedTeam.id);
    }
  }, [selectedTeam]);

  const loadPlayers = async (teamId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('team_id', teamId)
        .eq('role', 'player')
        .order('full_name');

      if (error) throw error;
      setPlayers(data || []);
    } catch (err) {
      console.error('Error loading players:', err);
      setError(err.message);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      // Load profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;
      setProfiles(profilesData || []);

      // Load organizations
      const { data: orgsData, error: orgsError } = await supabase
        .from('organizations')
        .select('*')
        .order('name');

      if (orgsError) throw orgsError;
      setOrganizations(orgsData || []);

      // Load teams
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('*, organizations(name)')
        .order('name');

      if (teamsError) throw teamsError;
      setTeams(teamsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile.id);
    setFormData({
      id: profile.id,
      email: profile.email,
      full_name: profile.full_name || '',
      role: profile.role || 'player',
      organization_id: profile.organization_id || '',
      team_id: profile.team_id || '',
      family_id: profile.family_id || '',
      birth_date: profile.birth_date || '',
      phone: profile.phone || '',
    });
    setShowCreateForm(false);
    setError('');
    setSuccess('');
  };

  const handleCreate = () => {
    setShowCreateForm(true);
    setEditingProfile(null);
    setFormData({
      id: '',
      email: '',
      full_name: '',
      role: 'player',
      organization_id: organizations[0]?.id || '',
      team_id: '',
      family_id: '',
      birth_date: '',
      phone: '',
    });
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setEditingProfile(null);
    setShowCreateForm(false);
    setFormData({
      id: '',
      email: '',
      full_name: '',
      role: 'player',
      organization_id: '',
      team_id: '',
      family_id: '',
      birth_date: '',
      phone: '',
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingProfile) {
        // Update existing profile
        const { error } = await supabase
          .from('profiles')
          .update({
            full_name: formData.full_name,
            role: formData.role,
            organization_id: formData.organization_id || null,
            team_id: formData.team_id || null,
            family_id: formData.family_id || null,
            birth_date: formData.birth_date || null,
            phone: formData.phone || null,
          })
          .eq('id', editingProfile);

        if (error) throw error;
        setSuccess('Profile updated successfully!');
      } else {
        // Create new profile
        const { error } = await supabase.from('profiles').insert({
          id: formData.id,
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          organization_id: formData.organization_id || null,
          team_id: formData.team_id || null,
          family_id: formData.family_id || null,
          birth_date: formData.birth_date || null,
          phone: formData.phone || null,
        });

        if (error) throw error;
        setSuccess('Profile created successfully!');
      }

      await loadData();
      handleCancel();
    } catch (error) {
      console.error('Error saving profile:', error);
      setError(error.message);
    }
  };

  const handleDelete = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this profile?')) {
      return;
    }

    try {
      const { error } = await supabase.from('profiles').delete().eq('id', profileId);

      if (error) throw error;
      setSuccess('Profile deleted successfully!');
      await loadData();
    } catch (error) {
      console.error('Error deleting profile:', error);
      setError(error.message);
    }
  };

  const handleImpersonate = async (targetProfile) => {
    // Only super_admins can impersonate
    if (realProfile?.role !== 'super_admin') {
      setError('Only super admins can impersonate users');
      return;
    }

    // Confirm before impersonating
    if (
      !window.confirm(
        `Impersonate ${targetProfile.full_name || targetProfile.email}?\n\nYou will see the application as they see it.`
      )
    ) {
      return;
    }

    try {
      const result = await startImpersonation(targetProfile.id);
      if (result.success) {
        setSuccess(`Now viewing as ${targetProfile.full_name || targetProfile.email}`);
        // Navigate to the impersonated user's default dashboard
        const defaultRoute = getDefaultRouteForRole(targetProfile.role);
        navigate(defaultRoute);
      } else {
        setError(result.error || 'Failed to start impersonation');
      }
    } catch (error) {
      console.error('Error impersonating user:', error);
      setError(error.message);
    }
  };

  const getDefaultRouteForRole = (role) => {
    switch (role) {
      case 'player':
        return '/player';
      case 'parent':
        return '/parent';
      case 'coach':
        return '/coach';
      case 'admin':
      case 'super_admin':
        return '/admin';
      default:
        return '/player';
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
    },
    header: {
      backgroundColor: 'white',
      borderBottom: '1px solid #e0e0e0',
      padding: isDesktop ? '20px 40px' : '16px',
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      justifyContent: 'space-between',
      alignItems: isDesktop ? 'center' : 'stretch',
      gap: isDesktop ? '0' : '12px',
      position: 'relative',
      zIndex: 100,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      position: 'relative',
      zIndex: 1001,
    },
    headerLogo: {
      height: isDesktop ? '56px' : '45px',
      width: isDesktop ? 'auto' : '45px',
      objectFit: 'contain',
    },
    mobileHeaderContainer: {
      display: 'grid',
      gridTemplateColumns: '45px 1fr 45px',
      alignItems: 'center',
      width: '100%',
      gap: '12px',
    },
    mobileHeaderTitle: {
      textAlign: 'center',
      overflow: 'visible',
    },
    title: {
      fontSize: isDesktop ? '24px' : '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    headerButtons: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      gap: '10px',
    },
    button: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    primaryButton: {
      backgroundColor: '#007bff',
    },
    dangerButton: {
      backgroundColor: '#dc3545',
    },
    content: {
      padding: isDesktop ? '40px' : '16px',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '30px' : '16px',
      marginBottom: isDesktop ? '20px' : '12px',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    cardTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    tableContainer: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '600px',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      borderBottom: '2px solid #e0e0e0',
      fontWeight: '600',
      color: '#666',
      fontSize: '14px',
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '14px',
      maxWidth: '200px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'uppercase',
    },
    form: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
      gap: '20px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
    },
    input: {
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
    },
    select: {
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      backgroundColor: 'white',
    },
    teamSelector: {
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      backgroundColor: 'white',
      width: '100%',
      maxWidth: '400px',
    },
    formActions: {
      gridColumn: '1 / -1',
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
      marginTop: '10px',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #fcc',
      marginBottom: '20px',
    },
    success: {
      padding: '12px',
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #cfc',
      marginBottom: '20px',
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
    },
    smallButton: {
      padding: isDesktop ? '6px 12px' : '10px 16px',
      fontSize: isDesktop ? '12px' : '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    editButton: {
      backgroundColor: '#007bff',
      color: 'white',
    },
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
    },
    impersonateButton: {
      backgroundColor: '#6f42c1',
      color: 'white',
    },
    profileCardsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    burgerButton: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
      width: '44px',
      height: '44px',
      backgroundColor: '#007bff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '12px 10px',
      zIndex: 10,
      transition: 'background-color 0.2s',
    },
    burgerLine: {
      width: '24px',
      height: '2px',
      backgroundColor: 'white',
      borderRadius: '2px',
      transition: 'all 0.3s linear',
    },
  };

  const getRoleBadgeStyle = (role) => {
    const colors = {
      admin: { bg: '#e3f2fd', color: '#1976d2' },
      coach: { bg: '#f3e5f5', color: '#7b1fa2' },
      player: { bg: '#e8f5e9', color: '#388e3c' },
      parent: { bg: '#fff3e0', color: '#f57c00' },
      super_admin: { bg: '#f3e5f5', color: '#4a148c' },
    };
    const style = colors[role] || colors.player;
    return {
      ...styles.badge,
      backgroundColor: style.bg,
      color: style.color,
    };
  };

  const getTeamName = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team ? team.name : 'Not assigned';
  };

  const getOrgName = (orgId) => {
    const org = organizations.find((o) => o.id === orgId);
    return org ? org.name : 'Not assigned';
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={styles.container}>
      <ImpersonationBanner />
      <header style={styles.header}>
        {isDesktop ? (
          <>
            <div style={styles.headerLeft}>
              <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
              <h1 style={styles.title}>Admin Dashboard</h1>
            </div>
            <div style={styles.headerButtons}>
              <DashboardNavigation />
            </div>
          </>
        ) : (
          <>
            <div style={styles.mobileHeaderContainer}>
              <img src="/logo.png" alt="Reps Over Rings" style={styles.headerLogo} />
              <div style={styles.mobileHeaderTitle}>
                <MobileHeaderDropdown />
              </div>
              <AvatarMenu />
            </div>
          </>
        )}
      </header>

      <DashboardTabbedNavigation
        sections={sections}
        activeSection={activeSection}
        activeSubTab={activeSubTab}
        onSectionChange={setActiveSection}
        onSubTabChange={setActiveSubTab}
        isDesktop={isDesktop}
      />

      <div style={styles.content}>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {/* Team Roster Tab */}
        {activeSection === 'roster' && (
          <>
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Select Team</h2>
                <select
                  style={styles.teamSelector}
                  value={selectedTeam?.id || ''}
                  onChange={(e) => {
                    const team = teams.find((t) => t.id === e.target.value);
                    setSelectedTeam(team);
                  }}
                >
                  <option value="">Select a team...</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name} ({team.organizations?.name})
                    </option>
                  ))}
                </select>
              </div>

              {selectedTeam && players.length > 0 && (
                <div>
                  <h3 style={styles.cardTitle}>
                    Team Roster: {selectedTeam.name} ({players.length} players)
                  </h3>

                  {isDesktop ? (
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Name</th>
                          <th style={styles.th}>Email</th>
                          <th style={styles.th}>Birth Date</th>
                          <th style={styles.th}>Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {players.map((player) => (
                          <tr key={player.id}>
                            <td style={styles.td}>{player.full_name}</td>
                            <td style={styles.td}>{player.email}</td>
                            <td style={styles.td}>{player.birth_date || 'N/A'}</td>
                            <td style={styles.td}>{player.phone || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div>
                      {players.map((player) => (
                        <PlayerCard key={player.id} player={player} isDesktop={false} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* User Management Tab */}
        {activeSection === 'users-access' && activeSubTab === 'users' && (
          <>
            {editingProfile || showCreateForm ? (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>
                    {editingProfile ? 'Edit Profile' : 'Create New Profile'}
                  </h2>
                </div>
                <form onSubmit={handleSubmit} style={styles.form}>
                  {showCreateForm && (
                    <>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>User ID (from Supabase Auth) *</label>
                        <input
                          type="text"
                          style={styles.input}
                          value={formData.id}
                          onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                          placeholder="UUID from auth.users"
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Email *</label>
                        <input
                          type="email"
                          style={styles.input}
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="user@example.com"
                          required
                        />
                      </div>
                    </>
                  )}

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Full Name *</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Role *</label>
                    <select
                      style={styles.select}
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                    >
                      <option value="player">Player</option>
                      <option value="parent">Parent</option>
                      <option value="coach">Coach</option>
                      <option value="admin">Admin</option>
                      {profile?.role === 'super_admin' && (
                        <option value="super_admin">Super Admin</option>
                      )}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Organization</label>
                    <select
                      style={styles.select}
                      value={formData.organization_id}
                      onChange={(e) =>
                        setFormData({ ...formData, organization_id: e.target.value })
                      }
                    >
                      <option value="">Not assigned</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Team</label>
                    <select
                      style={styles.select}
                      value={formData.team_id}
                      onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                    >
                      <option value="">Not assigned</option>
                      {teams
                        .filter(
                          (team) =>
                            !formData.organization_id ||
                            team.organization_id === formData.organization_id
                        )
                        .map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.name}{' '}
                            {team.organizations?.name ? `(${team.organizations.name})` : ''}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Family ID (for linking parents/children)</label>
                    <input
                      type="text"
                      style={styles.input}
                      value={formData.family_id}
                      onChange={(e) => setFormData({ ...formData, family_id: e.target.value })}
                      placeholder="e.g., smith-family"
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Birth Date</label>
                    <input
                      type="date"
                      style={styles.input}
                      value={formData.birth_date}
                      onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone</label>
                    <input
                      type="tel"
                      style={styles.input}
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div style={styles.formActions}>
                    <button
                      type="button"
                      style={{ ...styles.button, backgroundColor: '#6c757d' }}
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button type="submit" style={{ ...styles.button, ...styles.primaryButton }}>
                      {editingProfile ? 'Update Profile' : 'Create Profile'}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>User Profiles ({profiles.length})</h2>
                  <button
                    style={{ ...styles.button, ...styles.primaryButton }}
                    onClick={handleCreate}
                  >
                    + Create Profile
                  </button>
                </div>

                {isDesktop ? (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Name</th>
                          <th style={styles.th}>Email</th>
                          <th style={styles.th}>Role</th>
                          <th style={styles.th}>Organization</th>
                          <th style={styles.th}>Team</th>
                          <th style={styles.th}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profiles.map((profile) => (
                          <tr key={profile.id}>
                            <td style={styles.td}>{profile.full_name || 'Not set'}</td>
                            <td style={styles.td}>{profile.email}</td>
                            <td style={styles.td}>
                              <span style={getRoleBadgeStyle(profile.role)}>{profile.role}</span>
                            </td>
                            <td style={styles.td}>{getOrgName(profile.organization_id)}</td>
                            <td style={styles.td}>{getTeamName(profile.team_id)}</td>
                            <td style={styles.td}>
                              <div style={styles.actionButtons}>
                                <button
                                  style={{ ...styles.smallButton, ...styles.editButton }}
                                  onClick={() => handleEdit(profile)}
                                >
                                  Edit
                                </button>
                                <button
                                  style={{ ...styles.smallButton, ...styles.deleteButton }}
                                  onClick={() => handleDelete(profile.id)}
                                >
                                  Delete
                                </button>
                                {realProfile?.role === 'super_admin' &&
                                  profile.role !== 'super_admin' && (
                                    <button
                                      style={{ ...styles.smallButton, ...styles.impersonateButton }}
                                      onClick={() => handleImpersonate(profile)}
                                    >
                                      Impersonate
                                    </button>
                                  )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {profiles.length === 0 && (
                          <tr>
                            <td
                              colSpan="6"
                              style={{ ...styles.td, textAlign: 'center', color: '#999' }}
                            >
                              No profiles found. Create one to get started.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={styles.profileCardsContainer}>
                    {profiles.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        No profiles found. Create one to get started.
                      </div>
                    ) : (
                      profiles.map((profile) => (
                        <UserProfileCard
                          key={profile.id}
                          profile={profile}
                          organizations={organizations}
                          teams={teams}
                          onEdit={handleEdit}
                          onDelete={() => handleDelete(profile.id)}
                          onImpersonate={handleImpersonate}
                          showImpersonate={
                            realProfile?.role === 'super_admin' && profile.role !== 'super_admin'
                          }
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Users & Access Section */}
        {activeSection === 'users-access' && activeSubTab === 'invites' && (
          <InviteManager profile={profile} organizations={organizations} teams={teams} />
        )}

        {activeSection === 'users-access' && activeSubTab === 'parent-invites' && (
          <ParentInviteManager profile={profile} teams={teams} />
        )}

        {activeSection === 'users-access' && activeSubTab === 'player-tags' && (
          <PlayerTagManager profile={profile} />
        )}

        {/* Organization Settings Section */}
        {activeSection === 'org-settings' && activeSubTab === 'organization' && (
          <OrganizationManager
            profile={profile}
            organizations={organizations}
            teams={teams}
            onUpdate={loadData}
          />
        )}

        {activeSection === 'org-settings' && activeSubTab === 'stat-categories' && (
          <StatCategoryManager profile={profile} />
        )}

        {/* Drills Section */}
        {activeSection === 'drills' && activeSubTab === 'library' && (
          <DrillLibraryManager profile={profile} />
        )}

        {activeSection === 'drills' && activeSubTab === 'routines' && (
          <DrillRoutineManager profile={profile} />
        )}

        {/* Performance Section */}
        {activeSection === 'performance' && activeSubTab === 'tracking' && (
          <AssignmentTrackingTab
            selectedTeam={null}
            coachId={profile.id}
            isDesktop={isDesktop}
            isAdmin={true}
          />
        )}

        {activeSection === 'performance' && activeSubTab === 'metrics' && (
          <PerformanceMetricsDashboard profile={profile} teams={teams} />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminPage;
